import type {SyntheticEvent} from 'react'
import {GenericSelector} from '@freckle/educator-entities/ts/common/components/generic-selector'
import type {LangT} from '@freckle/educator-entities/ts/common/helpers/languages'
import {Languages} from '@freckle/educator-entities/ts/common/helpers/languages'
import {createSelectorOptionAsItem} from '@freckle/educator-entities/ts/common/logic/generic-selector'
import type {RlStandardSetT} from '@freckle/educator-entities/ts/common/models/rl-standard-set'
import {filterStandardSetsForDarkTest} from '@freckle/educator-entities/ts/common/models/rl-standard-set'
import {ButtonAsync} from '@freckle/educator-materials/ts/components/button-async'
import {Label} from '@freckle/educator-materials/ts/components/label'
import {LanguageDropdown} from '@freckle/educator-materials/ts/components/teacher-language-dropdown'
import {H1} from '@freckle/educator-materials/ts/components/typography'
import {useSafeTranslation} from '@freckle/educator-materials/ts/helpers/translate'
import {SafeTrans} from '@freckle/educator-materials/ts/helpers/translate/component'
import type {SchoolAdminAttrs} from '@freckle/school/ts/users/models/school-admin'
import {
  languagesItem,
  settingsPage,
  labelDescriptor,
  mathSelector,
  submitBtn,
} from './settings.module.scss'

type Props = {
  mathStandardSets: Array<RlStandardSetT>
  schoolAdmin: SchoolAdminAttrs
  selectedMathStandardSetId: string | undefined | null
  selectedLanguage: LangT
  onSubmit: (e: SyntheticEvent<EventTarget>) => Promise<unknown>
  onChangeMathStandardSetId: (selectedMathStandardSetId?: string | null) => void
  onChangeLanguage: (selectedLanguage: LangT) => void
}

export default function SettingsComponent(props: Props) {
  const {t} = useSafeTranslation()
  const {
    mathStandardSets,
    schoolAdmin,
    selectedMathStandardSetId,
    selectedLanguage,
    onSubmit,
    onChangeMathStandardSetId,
    onChangeLanguage,
  } = props

  const adminStandardSets = filterStandardSetsForDarkTest(
    mathStandardSets,
    schoolAdmin.email,
    selectedMathStandardSetId,
  )

  const mathStandardSetOptions = [
    createSelectorOptionAsItem(null, t('SETTINGS_PAGE_SELECTOR_OPTION_DEFAULT_TEXT')),
    ...adminStandardSets.map(mathStandardSet =>
      createSelectorOptionAsItem(mathStandardSet.id, mathStandardSet.description),
    ),
  ]

  return (
    <div className={settingsPage}>
      <H1>{t('SETTINGS_PAGE_TITLE')}</H1>

      <form role="form" onSubmit={onSubmit}>
        <div className="form-group">
          <Label htmlFor="mathStandardSetSelector">{t('SETTINGS_PAGE_MATH_SYSTEM_TEXT')}</Label>
          <div className={labelDescriptor}>
            <SafeTrans i18nKey="SETTINGS_PAGE_MATH_SYSTEM_DESCRIPTION_TEXT" />
          </div>
          <GenericSelector
            id="mathStandardSetSelector"
            selection={selectedMathStandardSetId}
            collection={mathStandardSetOptions}
            onChangeSelection={onChangeMathStandardSetId}
            className={mathSelector}
          />
          <div className={languagesItem}>
            <LanguageDropdown
              updateMode={{tag: 'component-is-passthrough'}}
              perspectiveLanguage={selectedLanguage}
              selectedLanguage={selectedLanguage}
              onChange={onChangeLanguage}
              labels={{
                formLabel: t('LANGUAGE_DROPDOWN_LABEL'),
                loadingText: t('LOADING_LANGUAGES_DROPDOWN_OPTION'),
              }}
              filterLanguages={schoolAdmin.language === Languages.Es ? [] : [Languages.Es]}
            />
          </div>
          <ButtonAsync onClick={onSubmit} type="submit" style="primary" className={submitBtn}>
            {t('SETTINGS_PAGE_SUBMIT_BTN_TEXT')}
          </ButtonAsync>
        </div>
      </form>
    </div>
  )
}
